import React from 'react';
import { useNavigate } from 'react-router-dom';
import { clearSession } from '../auth';
import {
  ArrowUpRight,
  ChefHat,
  LogOut,
  MonitorSmartphone,
} from 'lucide-react';

const cx = (...parts) => parts.filter(Boolean).join(' ');

const MODULES = [
  {
    title: 'Présentoir de cuisine',
    description: 'Suivez les commandes en cours, les tickets prêts en temps réel.',
    path: '/menu-selection',
    Icon: ChefHat,
    tag: 'Temps réel',
    liveDot: true,
    iconWrap: 'bg-gradient-to-br from-rose-500 to-orange-500 shadow-[0_8px_20px_rgba(244,63,94,0.35)] group-hover:shadow-[0_12px_28px_rgba(244,63,94,0.45)]',
    hoverBorder: 'hover:border-rose-300',
    hoverBg: 'hover:bg-gradient-to-br hover:from-rose-50 hover:to-orange-50/60',
    hoverShadow: 'hover:shadow-[0_20px_40px_rgba(244,63,94,0.18)]',
    ringHover: 'group-hover:ring-rose-200/80',
    arrowBg: 'bg-rose-100 text-rose-600 group-hover:scale-110 group-hover:bg-rose-500 group-hover:text-white group-hover:shadow-[0_8px_20px_rgba(244,63,94,0.35)]',
  },
];

const Dashboard = () => {
  const navigate = useNavigate();

  const handleLogout = () => {
    clearSession();
    navigate('/');
  };

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-[#eef2f7] font-sans text-kd-ink">
      {/* Background */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(244,63,94,0.08),transparent_42%),radial-gradient(circle_at_80%_0%,rgba(37,99,235,0.1),transparent_38%),radial-gradient(circle_at_50%_100%,rgba(148,163,184,0.12),transparent_45%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.07)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.07)_1px,transparent_1px)] bg-[size:28px_28px]" />
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-white/60 bg-white/75 shadow-header backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <img
            src="/lacaisse_logo.svg"
            alt="La Caisse"
            className="h-auto w-[170px] shrink-0 sm:w-[210px]"
          />
          <button
            type="button"
            className="kd-icon-btn border-slate-800 bg-slate-800 text-white hover:border-slate-900 hover:bg-slate-900"
            onClick={handleLogout}
            title="Déconnexion"
            aria-label="Déconnexion"
          >
            <LogOut aria-hidden="true" />
            <span className="hidden sm:inline">Quitter</span>
          </button>
        </div>
      </header>

      {/* Main */}
      <main className="relative z-10 flex flex-1 items-center justify-center px-4 py-8 sm:px-6 sm:py-12">
        <div className="w-full max-w-4xl animate-kd-fade-in">
          {/* Hub panel */}
          <div className="overflow-hidden rounded-[28px] border border-white/80 bg-white/90 shadow-[0_20px_60px_rgba(15,23,42,0.08)] backdrop-blur-sm">
            {/* Panel header */}
            <div className="border-b border-kd-line/70 bg-gradient-to-r from-slate-50/90 via-white to-slate-50/90 px-6 py-7 sm:px-8 sm:py-8">
              <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-2">
                  <h1 className="text-2xl font-extrabold tracking-tight text-kd-ink sm:text-[2rem]">
                    Tableau de bord
                  </h1>
                  <p className="max-w-xl text-sm leading-relaxed text-kd-soft sm:text-[15px]">
                    Sélectionnez un module pour accéder à votre espace de travail cuisine.
                  </p>
                </div>

                <div className="hidden shrink-0 sm:flex sm:h-[88px] sm:w-[88px] sm:items-center sm:justify-center sm:rounded-2xl sm:bg-gradient-to-br sm:from-slate-100 sm:to-slate-200/80 sm:ring-1 sm:ring-kd-line/80">
                  <MonitorSmartphone className="h-10 w-10 text-slate-500" strokeWidth={1.75} aria-hidden="true" />
                </div>
              </div>
            </div>

            {/* Modules section */}
            <div className="px-6 py-6 sm:px-8 sm:py-7">
              <div className="mx-auto grid max-w-2xl grid-cols-1 gap-4 sm:gap-5">
                {MODULES.map((module, index) => {
                  const { Icon } = module;
                  return (
                    <button
                      key={module.path}
                      type="button"
                      onClick={() => navigate(module.path)}
                      style={{ animationDelay: `${index * 120}ms` }}
                      className={cx(
                        'group relative flex h-full flex-col overflow-hidden rounded-2xl border border-kd-line/80 bg-white p-5 text-left ring-1 ring-transparent sm:p-6',
                        'animate-kd-card-show cursor-pointer',
                        'transition-all duration-300 ease-out',
                        'hover:-translate-y-2 hover:scale-[1.02] hover:animate-none active:scale-[0.98]',
                        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-kd-brand',
                        module.hoverBorder,
                        module.hoverBg,
                        module.hoverShadow,
                        module.ringHover
                      )}
                    >
                      {/* Hover shimmer */}
                      <span
                        className="pointer-events-none absolute inset-0 z-0 overflow-hidden opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                        aria-hidden="true"
                      >
                        <span className="absolute inset-y-0 w-1/2 -translate-x-full bg-gradient-to-r from-transparent via-white/70 to-transparent group-hover:animate-kd-shimmer" />
                      </span>
                      <span
                        className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full bg-white/40 opacity-0 blur-2xl transition duration-500 group-hover:opacity-100"
                        aria-hidden="true"
                      />

                      <span
                        className={cx(
                          'relative z-10 mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl text-white transition-all duration-300',
                          'group-hover:scale-110 group-hover:-rotate-3',
                          module.iconWrap
                        )}
                      >
                        <Icon
                          className="h-7 w-7 transition-transform duration-300 group-hover:scale-110"
                          strokeWidth={2}
                          aria-hidden="true"
                        />
                      </span>

                      <span className="relative z-10 mb-1 flex flex-wrap items-center gap-2">
                        <span className="text-base font-bold text-kd-ink transition-colors duration-300 group-hover:text-kd-ink sm:text-lg">
                          {module.title}
                        </span>
                        {module.tag ? (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-kd-wash px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-kd-soft ring-1 ring-kd-line/70 transition-all duration-300 group-hover:bg-white group-hover:shadow-sm">
                            {module.liveDot ? (
                              <span className="relative flex h-2 w-2 shrink-0" aria-hidden="true">
                                <span className="absolute inline-flex h-full w-full animate-kd-pulse-dot rounded-full bg-green-300 opacity-70" />
                                <span className="relative inline-flex h-2 w-2 rounded-full bg-green-400 shadow-[0_0_6px_rgba(74,222,128,0.85)]" />
                              </span>
                            ) : null}
                            {module.tag}
                          </span>
                        ) : null}
                      </span>
                      <span className="relative z-10 mb-5 flex-1 text-sm leading-relaxed text-kd-soft transition-colors duration-300 group-hover:text-kd-ink/70">
                        {module.description}
                      </span>

                      <span className="relative z-10 mt-auto flex items-center justify-end">
                        <span
                          className={cx(
                            'inline-flex h-10 w-10 items-center justify-center rounded-xl transition-all duration-300',
                            module.arrowBg
                          )}
                        >
                          <ArrowUpRight
                            className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                            strokeWidth={2.25}
                            aria-hidden="true"
                          />
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Panel footer */}
            <div className="border-t border-kd-line/70 bg-slate-50/70 px-6 py-4 sm:px-8">
              <p className="text-center text-xs font-medium text-kd-muted">
                Lacaisse.ma
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
